import React, { useCallback, useEffect, useState } from 'react';
import Autocomplete, { createFilterOptions, highlightSubstring } from '../Autocomplete';
import { index as searchApi } from '@afosto/content/api/Index';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import getAddressString from '@afosto/utils/getAddressString';

const AddressSearch = ({ name, label, ...otherProps }) => {
  const [addresses, setAddresses] = useState([]);
  const [isFetchingAddresses, setIsFetchingAddresses] = useState(true);

  const searchAddresses = useCallback(async searchValue => {
    setIsFetchingAddresses(true);
    try {
      const response = await searchApi.search({
        codes: ['rel'],
        models: ['addresses'],
        namespaces: ['default'],
        q: searchValue?.length > 0 ? [searchValue] : null,
      });
      const { data = {} } = response;
      const { hits = [] } = data;
      const suggestions = hits.reduce((acc, hit) => {
        const { title, reference, metadata = {}, ...rest } = hit;
        return [
          ...acc,
          {
            ...rest,
            ...metadata,
            id: reference,
            label: getAddressString(metadata),
            value: reference,
          },
        ];
      }, []);
      setAddresses(suggestions);
    } catch (error) {
      setAddresses([]);
    } finally {
      setIsFetchingAddresses(false);
    }
  }, []);

  useEffect(() => {
    searchAddresses();
  }, [searchAddresses]);

  /**
   * Search addresses based on the input value.
   * @param {Event} event
   */
  const handleSearchAddresses = event => {
    const { value } = event?.target ?? {};

    searchAddresses(value).catch(() => {
      // Do something with error.
    });
  };

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: option => `${option.label} ${option.description ?? ''}`,
  });

  const renderAddressOption = (option, { inputValue = '' } = {}) => {
    let optionLabel = option;

    if (typeof option === 'object') {
      // eslint-disable-next-line prefer-destructuring
      optionLabel = option.label;
    }
    return (
      <ListItem component="div" dense disableGutters>
        <ListItemText
          primary={highlightSubstring(optionLabel, inputValue)}
          secondary={highlightSubstring(option.description, inputValue)}
        />
      </ListItem>
    );
  };

  return (
    <Autocomplete
      {...otherProps}
      name={name}
      label={label}
      filterOptions={filterOptions}
      onInputChange={handleSearchAddresses}
      options={addresses}
      loading={isFetchingAddresses}
      renderOption={renderAddressOption}
    />
  );
};

AddressSearch.propTypes = Autocomplete.propTypes;

AddressSearch.defaultProps = Autocomplete.defaultProps;

export default AddressSearch;
